import type { JSX } from 'react';
import { FileText, FileCheck, Ruler, Truck, GanttChart, BookOpen, Anchor } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useContent } from '@data';
import { useT } from '@/i18n';
import { useInView } from '@hooks/useInView';
import { useState } from 'react';
import { SectionHeader, RevealWrapper, Tag, ImageZoom } from '@components/ui';
import './AsterionApps.css';

type IconComp = React.ComponentType<LucideProps>;

const ICON_MAP = { FileText, FileCheck, Ruler, Truck, GanttChart, BookOpen, Anchor } satisfies Record<string, IconComp>;

function isKnownIcon(name: string): name is keyof typeof ICON_MAP {
  return name in ICON_MAP;
}

interface AppCardHeaderProps {
  app: { logo?: string; logoFull?: string; isDraft?: boolean };
  Icon: IconComp | null;
}

function AppCardHeader({ app, Icon }: AppCardHeaderProps): JSX.Element {
  return (
    <div className="AppCard__header">
      {app.logoFull !== undefined ? (
        <span className="AppCard__logofull-wrap" aria-hidden="true">
          <img className="AppCard__logofull AppCard__logo--light" src={`/asterion/${app.logoFull}.svg`} alt="" loading="lazy" />
          <img className="AppCard__logofull AppCard__logo--dark" src={`/asterion/${app.logoFull}-dark.svg`} alt="" loading="lazy" />
        </span>
      ) : (
        <span className="AppCard__icon" aria-hidden="true">
          {app.logo !== undefined ? (
            <>
              <img className="AppCard__logo AppCard__logo--light" src={`/asterion/${app.logo}.svg`} alt="" loading="lazy" />
              <img className="AppCard__logo AppCard__logo--dark" src={`/asterion/${app.logo}-dark.svg`} alt="" loading="lazy" />
            </>
          ) : (
            Icon !== null && <Icon size={18} strokeWidth={1.5} />
          )}
        </span>
      )}
      {app.isDraft === true && (
        <span className="AppCard__draft-badge" aria-label="En développement">WIP</span>
      )}
    </div>
  );
}

export default function AsterionApps(): JSX.Element {
  const { ASTERION_APPS } = useContent();
  const t = useT();
  const [activeId, setActiveId] = useState<string>(ASTERION_APPS[0]?.id ?? '');
  /* Les captures ne portent pas `loading="lazy"` : l'IntersectionObserver dont
     il dépend est figé par Chrome sur cette page (défilement fluide + scroll-snap,
     même cause que le repli sur `scroll` de `useReveal`), et aucune capture ne
     se téléchargeait jamais. C'est donc le montage qui fait la temporisation —
     une capture n'est montée qu'une fois sa carte ouverte et la section
     approchée, puis n'est plus démontée. */
  const [sectionRef, sectionInView] = useInView<HTMLElement>();
  const [opened, setOpened] = useState<ReadonlySet<string>>(
    () => new Set<string>(ASTERION_APPS[0] === undefined ? [] : [ASTERION_APPS[0].id]),
  );

  function select(id: string): void {
    setActiveId(id);
    setOpened(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  }

  return (
    <section id="asterion" className="section AsterionApps" ref={sectionRef}>
      <SectionHeader
        chip={t.sections.asterion.chip}
        chipVariant="m"
        title={t.sections.asterion.title}
        subtitle={t.sections.asterion.subtitle}
      />

      <RevealWrapper delay={1}>
        <div className="AsterionApps__spotlight">
          <ul className="AsterionApps__list" role="list">
            {ASTERION_APPS.map(app => {
              const Icon = isKnownIcon(app.icon) ? ICON_MAP[app.icon] : null;
              return (
                <li key={app.id}>
                  <button
                    type="button"
                    className={`AsterionApps__list-btn${app.id === activeId ? ' AsterionApps__list-btn--active' : ''}`}
                    style={{
                      '--app-color': app.colorVar,
                      '--app-color-rgb': app.colorRgbVar,
                    } as React.CSSProperties}
                    aria-pressed={app.id === activeId}
                    onClick={() => { select(app.id); }}
                  >
                    {app.logo !== undefined ? (
                      <>
                        <img className="AppCard__logo AppCard__logo--light AsterionApps__list-logo" src={`/asterion/${app.logo}.svg`} alt="" loading="lazy" />
                        <img className="AppCard__logo AppCard__logo--dark AsterionApps__list-logo" src={`/asterion/${app.logo}-dark.svg`} alt="" loading="lazy" />
                      </>
                    ) : (
                      <span className="AsterionApps__list-icon" aria-hidden="true">
                        {Icon !== null && <Icon size={16} strokeWidth={1.5} />}
                      </span>
                    )}
                    <span className="AsterionApps__list-name">{app.name}</span>
                    {app.isDraft && <span className="AppCard__draft-badge">WIP</span>}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="AsterionApps__stage">
            {ASTERION_APPS.map(app => {
              const Icon = isKnownIcon(app.icon) ? ICON_MAP[app.icon] : null;
              return (
                <article
                  key={app.id}
                  className={`AppCard AppCard--spot${app.isDraft ? ' AppCard--draft' : ''}`}
                  style={{
                    '--app-color': app.colorVar,
                    '--app-color-rgb': app.colorRgbVar,
                  } as React.CSSProperties}
                  aria-labelledby={`app-${app.id}`}
                  hidden={app.id !== activeId}
                >
                  <AppCardHeader app={app} Icon={Icon} />
                  <Tag>{app.tag}</Tag>
                  <h3 className="AppCard__name" id={`app-${app.id}`}>{app.name}</h3>
                  <p className="AppCard__desc">{app.description}</p>
                  {app.screenshot !== undefined ? (
                    sectionInView && opened.has(app.id) && (
                      <ImageZoom
                        className="AppCard__shot"
                        src={app.screenshot}
                        alt={`${t.sections.asterion.shotAlt} ${app.name}`}
                        openLabel={`${t.sections.asterion.enlarge} — ${app.name}`}
                        closeLabel={t.sections.asterion.close}
                      />
                    )
                  ) : (
                    /* Même classe que la capture : la tuile occupe la case de
                       grille et le ratio des vraies captures, pour que la
                       mise en page ne change pas d'une app à l'autre. */
                    <div className="AppCard__shot AppCard__shot--soon">
                      {Icon !== null && (
                        <span className="AppCard__soon-icon" aria-hidden="true">
                          <Icon size={30} strokeWidth={1.25} />
                        </span>
                      )}
                      <span className="AppCard__soon-label">{t.sections.asterion.comingSoon}</span>
                    </div>
                  )}
                  <div className="AppCard__accent" aria-hidden="true" />
                </article>
              );
            })}
          </div>
        </div>
      </RevealWrapper>

      <div className="AsterionApps__cta">
        <a
          href="https://solutions-asterion.com"
          target="_blank"
          rel="noopener noreferrer"
          className="AsterionApps__cta-logo"
          aria-label="Asterion Solutions"
        >
          <img className="AsterionApps__logo AsterionApps__logo--light" src="/asterion/logo-asterion-solutions.svg" alt="Logo Asterion Solutions" loading="lazy" />
          <img className="AsterionApps__logo AsterionApps__logo--dark" src="/asterion/logo-asterion-solutions-dark.svg" alt="Logo Asterion Solutions" loading="lazy" />
        </a>
        <a
          href="https://solutions-asterion.com"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary AsterionApps__cta-btn"
        >
          {t.sections.asterion.cta}
        </a>
        <p className="AsterionApps__cta-sub">
          {t.sections.asterion.ctaSub}
        </p>
      </div>
    </section>
  );
}
